import { DangerText, MuteText } from '@elements/Common';
import { CommonUtility } from '@utility/common';
import {
  ArrowRightIcon,
  BoatIcon,
  CheckIcon,
  type Icon,
  type IconProps,
  PackageIcon,
  TruckIcon,
} from '@phosphor-icons/react';
import {
  cloneElement,
  Fragment,
  isValidElement,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
} from 'react';
import { cn } from '@skupreme/shared/ui';
import {
  DynamicFieldsV2,
  DynamicFieldType,
  fbaShipmentSteps,
  setDefaultDFValue,
  setDFError,
} from '@page-components/shipment';
import { LoaderBar } from '@components/LoaderBar';
import { ToastMessage } from '@utility/toast';
import { metadataHook } from '@hooks/metadata';
import { countryListHook } from '@hooks/list';
import { dropdownOptionsIncludeFlag } from '@elements/Flag';
import { ColoredPill } from '@elements/Pills';
import { HoverBorderButton } from '@elements/Button';
import { ArrowLeft } from '@phosphor-icons/react/dist/ssr';
import {
  ActiveStep,
  AnimateCheck,
  CheckMark,
  Step,
  Steps,
  WhiteOverlay,
} from './commonStyle';
import type { ShipmentDynamicFieldType } from '@hooks/shipment';
import { StepBanner, type StepBannerType } from './stepBanner';

/* Option icons the shipment metadata names; an unknown name renders none. */
const OPTION_ICONS: Record<string, Icon> = {
  Boat: BoatIcon,
  Package: PackageIcon,
  Truck: TruckIcon,
};

function WhiteBgLoader({ content }: { content?: string }) {
  return <LoaderBar content={content} className="sk:bg-surface!" />;
}

export interface IStepItem {
  stepName: string;
  stepText: string;
  disabled?: boolean;
  completed?: boolean;
  submit?: boolean;
  toastMessage?: string;
  fetchNeeded?: boolean;
  render?: ReactElement;
  button?: ReactElement;
}

interface IError {
  text: string;
}

interface IDynamicData {
  fba?: {
    currentState?: string;
  };
  fields: ShipmentDynamicFieldType[];
}

interface IStepperProps {
  actionAllowed?: boolean;
  actionButtonClick?: (item: ShipmentDynamicFieldType) => void;
  activeTab: number;
  apiDFValue?: Record<string, any>;
  bannerType?: StepBannerType;
  bannerMessage?: string;
  customLoaderBar?: ReactElement;
  dFValue?: Record<string, any>;
  documents?: any;
  dynamicData?: IDynamicData;
  loading?: boolean;
  loadingText?: string;
  removeDocument?: (doc: any) => void;
  setAPIDFValue?: (value: Record<string, any>) => void;
  setActiveTab: (index: number | ((prev: number) => number)) => void;
  setDFValue?: (value: Record<string, any>) => void;
  steps: IStepItem[];
  submit?: () => void;
  uploadDocument?: (doc: any) => void;
  errors?: IError[];
  borderColor?: string;
  className?: string;
  isFBA?: boolean;
}

function BorderLeft({ borderColor }: { borderColor?: string }) {
  return (
    <div
      className="sk:absolute sk:top-0 sk:left-0 sk:z-[1] sk:h-full sk:w-2.5 sk:rounded-l-[5px]"
      style={{ background: borderColor }}
    />
  );
}

export function Stepper({
  actionAllowed,
  actionButtonClick: actionOfButton,
  activeTab,
  apiDFValue,
  customLoaderBar,
  dFValue,
  documents,
  dynamicData,
  loading,
  loadingText,
  removeDocument,
  setAPIDFValue,
  setActiveTab,
  setDFValue,
  steps,
  submit,
  uploadDocument,
  errors,
  borderColor,
  className,
  bannerType,
  bannerMessage,
  isFBA,
}: IStepperProps) {
  const { fields: stepData } = dynamicData || {};

  const { metadata } = metadataHook();
  const [tabNames, setTabNames] = useState<Record<string, any>>({});
  const [dFError, setErrors] = useState<Record<string, any>>({});

  useEffect(() => {
    if (
      metadata &&
      'shipmentTabs' in metadata &&
      metadata.shipmentTabs?.leftTabs
    ) {
      setTabNames((metadata as any).shipmentTabs.leftTabs);
    }
  }, [metadata]);

  const [animate, setAnimate] = useState<boolean>(false);

  const moveToCurrentStep = (index: number) => {
    if (index <= activeTab) {
      setActiveTab(index);
    }
  };

  const moveToNextStep = () => {
    if (activeTab + 1 === steps.length || steps[activeTab]?.submit) {
      submit && submit();
    } else {
      playAnimation();
      setActiveTab(activeTab => activeTab + 1);
    }
  };

  const playAnimation = () => {
    if (steps[activeTab].toastMessage) {
      ToastMessage.success(steps[activeTab].toastMessage);
    }
    setAnimate(true);
    setTimeout(() => {
      setAnimate(false);
    }, 1000);
  };

  const dynamicFieldChanged = (key: string, value: any) => {
    let trimmedValue = value;
    if (trimmedValue && trimmedValue.trimStart) {
      trimmedValue = trimmedValue.trimStart();
      trimmedValue = trimmedValue.replace(/ {2}$/g, ' ');
    }

    setDFValue &&
      setDFValue({
        ...dFValue,
        [key]: trimmedValue,
      });

    const apiTrigger = stepData?.find(x => x.name === key && x.triggerApi);

    if (apiTrigger && setAPIDFValue) {
      setAPIDFValue({
        ...apiDFValue,
        [key]: trimmedValue,
      });
    }
  };

  useEffect(() => {
    let hasErrors = false;
    Object.values(dFError || {}).forEach(val => {
      if (Array.isArray(val)) {
        val.forEach(field => {
          if (typeof field === 'object') {
            if (Object.values(field || {}).some(x => !!x)) {
              hasErrors = true;
            }
          } else if (field) {
            hasErrors = true;
          }
        });
      } else if (val) {
        hasErrors = true;
      }
    });

    if (hasErrors) {
      const error = setDFError(stepData, dFValue);
      setErrors(error);
    }
  }, [dFValue, stepData]);

  const checkDFError = (errors: any): boolean => {
    if (typeof errors === 'object') {
      for (const value of Object.values(errors)) {
        const hasError = checkDFError(value);
        if (hasError) {
          return true;
        }
      }
    } else if (Array.isArray(errors)) {
      for (const error of errors) {
        const hasError = checkDFError(error);
        if (hasError) {
          return true;
        }
      }
    } else {
      return !!errors;
    }
    return false;
  };

  const actionButtonClick = (item: ShipmentDynamicFieldType) => {
    const error = setDFError(stepData, dFValue);
    const hasErrors = checkDFError(error);

    if (
      hasErrors &&
      !item.triggerDynamicApi &&
      !item.onChangeActionApi &&
      item.name !== 'deleteShipment' &&
      !item.showConfirmPopup
    ) {
      setErrors(error);
    } else if (actionOfButton) {
      actionOfButton(item);
    }
  };

  const addNestedField = (item: ShipmentDynamicFieldType) => {
    const temp = setDefaultDFValue(item.nestedFields, item.nestedFields);
    if (setDFValue && dFValue) {
      setDFValue({
        ...dFValue,
        [item.name]: [...dFValue[item.name], temp],
      });
    }
  };

  const removeNestedField = (name: string, index: number) => {
    if (!dFValue || !setDFValue) return;

    const temp = dFValue[name].filter(
      (_: any, vIndex: number) => index !== vIndex,
    );
    setDFValue({
      ...dFValue,
      [name]: temp,
    });
  };

  const isPrevousStepEnabled =
    !!steps?.[activeTab - 1] && !steps?.[activeTab - 1]?.disabled;

  const moveBack = () => {
    if (isPrevousStepEnabled) {
      setActiveTab(activeTab - 1);
    }
  };

  return (
    <div className={cn('sk:relative sk:h-full sk:w-full', className)}>
      {borderColor && <BorderLeft borderColor={borderColor} />}
      <Steps>
        {isPrevousStepEnabled && (
          <HoverBorderButton onClick={moveBack} className="icon-btn tiny">
            <ArrowLeft weight="bold" />
          </HoverBorderButton>
        )}
        {steps?.map((step, index) => (
          <Fragment key={step.stepName}>
            <Step
              onClick={() => !steps[index].disabled && moveToCurrentStep(index)}
              allowed={index <= activeTab && !steps[index].disabled ? 1 : 0}
            >
              {index < activeTab || steps[index].completed ? (
                <ColoredPill className="sk:py-0!" color="var(--sk-brand)">
                  <CheckMark size={15} />
                  {(tabNames && tabNames[step.stepName]?.text) || step.stepText}
                </ColoredPill>
              ) : activeTab === index ? (
                <ColoredPill className="sk:py-0!" color="var(--sk-fg)">
                  {(tabNames && tabNames[step.stepName]?.text) || step.stepText}
                </ColoredPill>
              ) : (
                <MuteText>
                  {(tabNames && tabNames[step.stepName]?.text) || step.stepText}
                </MuteText>
              )}
            </Step>
            {index + 1 < steps.length ? (
              <ArrowRightIcon className="icon sk:text-fg-tertiary" size={15} />
            ) : (
              <></>
            )}
          </Fragment>
        ))}
      </Steps>
      <ActiveStep>
        {loading &&
          (isValidElement(customLoaderBar) ? (
            cloneElement(customLoaderBar)
          ) : (
            <WhiteBgLoader content={loadingText || 'Loading'} />
          ))}
        {!steps[activeTab]?.fetchNeeded &&
          isValidElement(steps[activeTab]?.render) &&
          cloneElement(steps[activeTab].render!, {
            ...(steps[activeTab].render!.props || {}),
            moveToNextStep,
          } as any)}
        {CommonUtility.isValidArray(errors) && (
          <div className="sk:mx-3.5! sk:mb-3.5!">
            {errors!.map((error, idx) => {
              if (CommonUtility.isValidHTML(error.text)) {
                return (
                  <DangerText
                    key={idx}
                    dangerouslySetInnerHTML={{ __html: error.text }}
                  />
                );
              }
              return <DangerText key={idx}>{error.text}</DangerText>;
            })}
          </div>
        )}
        <StepBanner bannerMessage={bannerMessage} bannerType={bannerType} />
        {dynamicData?.fba?.currentState ===
          fbaShipmentSteps.transportOptions && (
          <StepBanner
            bannerMessage={
              <p>
                When confirming transportation, all shipments must use the same
                carrier. If your shipment is split, you can select different
                pickup dates, but the carrier must be the same for all
                shipments. Using different carriers will cause an error.
              </p>
            }
          />
        )}

        {steps[activeTab]?.fetchNeeded && (
          <StepRenderer
            actionAllowed={actionAllowed}
            actionButtonClick={actionButtonClick}
            addNestedField={addNestedField}
            button={steps[activeTab]?.button}
            dFError={dFError}
            dFValue={dFValue}
            documents={documents}
            dynamicFieldChanged={dynamicFieldChanged}
            moveToNextStep={moveToNextStep}
            removeDocument={removeDocument}
            removeNestedField={removeNestedField}
            stepData={stepData}
            uploadDocument={uploadDocument}
            isFBA={isFBA}
          />
        )}
      </ActiveStep>
      {animate && (
        <AnimateCheck>
          <CheckIcon size={60} />
          <WhiteOverlay />
        </AnimateCheck>
      )}
    </div>
  );
}

interface IStepRendererProps {
  actionAllowed?: boolean;
  actionButtonClick: (item: ShipmentDynamicFieldType) => void;
  addNestedField: (item: ShipmentDynamicFieldType) => void;
  button?: ReactElement;
  dFError: Record<string, any>;
  dFValue?: Record<string, any>;
  documents?: any;
  dynamicFieldChanged: (key: string, value: any) => void;
  moveToNextStep: () => void;
  readOnly?: boolean;
  removeDocument?: (doc: any) => void;
  removeNestedField: (name: string, index: number) => void;
  stepData?: ShipmentDynamicFieldType[];
  uploadDocument?: (doc: any) => void;
  isFBA?: boolean;
}

export function StepRenderer({
  actionAllowed,
  actionButtonClick,
  addNestedField,
  button,
  dFError,
  dFValue,
  documents,
  dynamicFieldChanged,
  moveToNextStep,
  readOnly,
  removeDocument,
  removeNestedField,
  stepData,
  uploadDocument,
  isFBA,
}: IStepRendererProps) {
  /* @ts-expect-error - TS2554: Expected 1 arguments, but got 0. */
  const { data } = countryListHook();

  const countryOptions = useMemo(
    () => dropdownOptionsIncludeFlag(data || []),
    [data],
  );

  const completed = useMemo(() => {
    if (!stepData) return false;

    for (const step of stepData) {
      if (!dFValue?.[step.name] && dFValue?.[step.name] !== false) {
        return false;
      }
    }
    return true;
  }, [dFValue, stepData]);

  const modStepData = useMemo(
    () =>
      stepData?.map(field => {
        if (field.intermediateButton) {
          field.intermediateButton = stepData.find(
            x => x.name === field.intermediateButton,
          );
        }
        if (field.inputType === DynamicFieldType.dropdown) {
          field.options = field.options!.map(opt => {
            const IconComp = opt.icon ? OPTION_ICONS[opt.icon] : undefined;
            const renderedIcon = IconComp ? <IconComp /> : undefined;
            opt.image = isValidElement(renderedIcon)
              ? cloneElement(renderedIcon as ReactElement<IconProps>, {
                  size: 17,
                  className: 'dropdown-icon',
                })
              : undefined;
            delete opt.icon;
            return opt;
          });
        }
        return field;
      }),
    [stepData, countryOptions],
  );

  if (!CommonUtility.isValidArray(modStepData))
    return <span className="sk:ml-2!">Nothing to show</span>;

  return (
    <>
      <DynamicFieldsV2
        actionAllowed={actionAllowed}
        actionButtonClick={actionButtonClick}
        addNestedField={addNestedField}
        countryOptions={countryOptions as any}
        data={modStepData}
        dimensionsUnit="in."
        documents={documents}
        errors={dFError}
        onChange={dynamicFieldChanged}
        readOnly={readOnly}
        removeDocument={removeDocument}
        removeNestedField={removeNestedField}
        uploadDocument={uploadDocument}
        values={dFValue}
        weightUnit="lb"
        isFBA={isFBA}
      />
      {isValidElement(button) &&
        cloneElement(
          button as ReactElement<any>,
          {
            disabled: !completed,
            onClick: moveToNextStep,
          } as any,
        )}
    </>
  );
}
